import { matchSorter } from "match-sorter"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { t } from "translations"

import Button from "@mui/material/Button"
import FormHelperText from "@mui/material/FormHelperText"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import useDebounce from "@nhi/hooks/useDebounce"
import { IDictionaryTerm } from "data/contentData/interfaces/mongodb/IDictionaryTerm"
import { rewrites } from "lib/routes"

import Icon from "Components/Icon/Icon"

import { GlossaryList } from "./GlossaryList"
import GlossaryListSkeleton from "./GlossaryListSkeleton"
import { IDictionaryItemWithMatch, IDictionarySearchInput } from "./Interfaces"

const alphabet = t.dictionary.alphabet

export default function GlossarySearch({
    dictionary
}: {
    dictionary: IDictionaryTerm[]
}) {
    const router = useRouter()
    const [filteredGlossary, setFilteredGlossary] =
        useState<IDictionaryItemWithMatch[]>()

    const [loading, setLoading] = useState(false)

    const [input, setInput] = useState<IDictionarySearchInput>({
        type: "letter",
        value: router.query.dictionary?.[0] ?? "a"
    })

    const debouncedInput = useDebounce(input, 200)

    // if history back is fired, the input is not updated, so we need to check the path and update the input
    useEffect(() => {
        const routerPath = decodeURI(router.asPath).split("/").pop() ?? ""
        if (router.asPath.includes(rewrites["/dictionary/"]))
            setInput({
                type: "letter",
                value: routerPath
            })
    }, [router.asPath])

    useEffect(() => {
        if (
            debouncedInput.value.length >= 1 ||
            debouncedInput.type === "letter"
        ) {
            const dictionarySearchFound = dictionarySearch(dictionary, {
                type: debouncedInput.type,
                value: debouncedInput.value
            })

            setFilteredGlossary(dictionarySearchFound)

            router.push(
                {
                    pathname: rewrites["/dictionary/"]
                },
                {
                    pathname: `${rewrites["/dictionary/"]}/${debouncedInput.value}`
                },
                {
                    shallow: true
                }
            )

            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dictionary, debouncedInput.value, debouncedInput.type])

    const [markedLetter, setMarkedLetter] = useState<string>(() =>
        input.type === "exact" ? input.value : ""
    )

    useEffect(() => {
        if (input.type === "letter") setMarkedLetter(input.value)
        else setMarkedLetter("")
    }, [input.type, input.value])

    const shouldShowResult = input.value.length >= 1 || input.type === "letter"

    const handleLetterClick = (letter: string) => {
        setLoading(true)
        setInput({
            type: "letter",
            value: letter.toLowerCase()
        })
    }

    const handleTermClick = (term: string) => () => {
        setLoading(true)
        setInput({
            type: "exact",
            value: term.toLowerCase()
        })
    }

    const handleClearSearch = () => {
        setInput({
            type: "letter",
            value: "a"
        })
    }

    const handleInputChange = (value: string) => {
        if (value.length < 3) setLoading(true)
        setInput({ type: "search", value })
    }

    return (
        <StyledWrapper>
            <StyledHeader variant="h2">{t.dictionary.title}</StyledHeader>
            <StyledForm>
                <TextField
                    fullWidth
                    variant="outlined"
                    label={t.dictionary.searchInputLabel}
                    InputProps={{
                        endAdornment: input.value.length ? (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Clear search"
                                    onClick={handleClearSearch}>
                                    <Icon name="clear" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined
                    }}
                    onChange={({ target: { value } }) =>
                        handleInputChange(value)
                    }
                    value={input.value}
                />
                <StyledHelperText>{t.dictionary.searchTips}</StyledHelperText>
            </StyledForm>
            <StyledAlphabetContainer>
                {alphabet.split("").map(letter => (
                    <div key={letter}>
                        <Button
                            onClick={_ => handleLetterClick(letter)}
                            variant={
                                markedLetter.toLowerCase() ===
                                letter.toLowerCase()
                                    ? "contained"
                                    : "outlined"
                            }>
                            {letter.toUpperCase()}
                        </Button>
                    </div>
                ))}
            </StyledAlphabetContainer>
            {shouldShowResult && filteredGlossary?.length ? (
                loading ? (
                    <GlossaryListSkeleton />
                ) : (
                    <GlossaryList
                        glossary={filteredGlossary}
                        handleTermClick={handleTermClick}
                    />
                )
            ) : (
                <StyledNoMatch variant="h5">
                    {t.dictionary.noMatch}
                </StyledNoMatch>
            )}
        </StyledWrapper>
    )
}

const dictionarySearch = (
    dictionary: IDictionaryTerm[],
    input: IDictionarySearchInput
) => {
    const result: IDictionaryItemWithMatch[] = []
    let predicate

    switch (input.type) {
        case "letter":
            predicate = (word: string) =>
                word.toLowerCase().startsWith(input.value.toLowerCase())
            break
        case "search":
            predicate = (word: string) =>
                word.toLowerCase().includes(input.value.toLowerCase())
            break
        default:
            predicate = (word: string) =>
                word.toLowerCase() === input.value.toLowerCase()
            break
    }

    for (const term of dictionary) {
        const matchIndex = term.words.findIndex(predicate)
        if (matchIndex !== -1)
            result.push({
                description: term.description,
                match: term.words[matchIndex],
                words: term.words.filter((_, i) => i !== matchIndex)
            })
    }

    return matchSorter(result, input.value, { keys: ["match"] })
}

const StyledWrapper = styled("div")(({ theme }) => ({
    background: theme.palette.common.white,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    margin: "0 auto"
}))

const StyledNoMatch = styled(Typography)(({ theme }) => ({
    margin: theme.spacing(4)
}))

const StyledHeader = styled(Typography)(({ theme }) => ({
    margin: theme.spacing(4)
}))

const StyledForm = styled("form")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    margin: `${theme.spacing(0)} ${theme.spacing(4)}`,
    alignItems: "center"
}))

const StyledHelperText = styled(FormHelperText)(({ theme }) => ({
    color: theme.palette.primary.dark
}))

const StyledAlphabetContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    margin: `${theme.spacing(1)} ${theme.spacing(4)}`
}))
